import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiUpload } from '../../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Send, Check, X, Plus, Trash2, Paperclip } from 'lucide-react';
import { formatINR } from '../../../lib/format';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600', pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-600',
  completed: 'bg-blue-100 text-blue-700', cancelled: 'bg-slate-100 text-slate-400',
};
const EXP_CATS = ['flight', 'hotel', 'meals', 'local_transport', 'other'];

export default function TravelRequestDetail() {
  const { rid } = useParams();
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [showExpense, setShowExpense] = useState(false);

  const load = useCallback(() => apiGet(`/hr/travel/requests/${rid}`).then(setReq).catch(() => toast.error('Not found')), [rid]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body, msg) => {
    try { await apiPost(`/hr/travel/requests/${rid}/${path}`, body || {}); toast.success(msg); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  if (!req) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-3xl" data-testid="travel-detail-page">
      <button onClick={() => nav('/app/hr/travel/requests')} className="text-xs text-slate-500 flex items-center gap-1 hover:text-[#3b4872]" data-testid="travel-back"><ArrowLeft size={14} /> All Requests</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">{req.from_location || '?'} → {req.to_location || '?'}</h1>
          <p className="text-sm text-slate-600 mt-1">{req.employee_name} · {req.request_type} · {req.mode}{req.purpose ? ` · ${req.purpose}` : ''}</p>
          <span className={`inline-block mt-2 text-[11px] font-display font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm ${STATUS_COLORS[req.status]}`} data-testid="travel-status">{req.status.replace('_', ' ')}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {req.status === 'draft' && <button onClick={() => act('submit', {}, 'Submitted for approval')} data-testid="submit-travel-btn" className="px-3 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider flex items-center gap-1.5"><Send size={13} /> Submit</button>}
          {req.status === 'pending_approval' && req.can_approve && (
            <>
              <button onClick={() => { const n = window.prompt('Approval notes (optional):', ''); if (n !== null) act('approve', { approval_notes: n }, 'Approved'); }} data-testid="approve-travel-btn" className="px-3 py-2 bg-emerald-600 text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider flex items-center gap-1.5"><Check size={13} /> Approve</button>
              <button onClick={() => { const r = window.prompt('Rejection reason:'); if (r && r.trim()) act('reject', { rejection_reason: r.trim() }, 'Rejected'); }} data-testid="reject-travel-btn" className="px-3 py-2 border border-red-300 text-red-600 rounded-sm text-xs font-display font-bold uppercase tracking-wider flex items-center gap-1.5"><X size={13} /> Reject</button>
            </>
          )}
          {req.status === 'approved' && <button onClick={() => setShowExpense(true)} data-testid="claim-expense-btn" className="px-3 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider flex items-center gap-1.5"><Plus size={13} /> Claim Expense</button>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Info label="Departure" v={req.departure_date} />
        <Info label="Return" v={req.return_date} />
        <Info label="Estimated Cost" v={req.estimated_cost ? formatINR(req.estimated_cost) : null} />
        <Info label="Advance Requested" v={req.advance_requested ? formatINR(req.advance_requested) : null} />
        <Info label="Advance Paid" v={req.advance_paid != null ? formatINR(req.advance_paid) : null} />
        <Info label="Accommodation" v={req.accommodation_required ? 'Required' : 'Not required'} />
        <Info label="Approver / Manager" v={req.reporting_manager_name} />
        {req.approval_notes && <Info label="Approval Notes" v={req.approval_notes} />}
        {req.rejection_reason && <Info label="Rejection Reason" v={req.rejection_reason} />}
      </div>

      {/* Expense claims */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-4 py-3 border-b"><h3 className="text-xs font-display font-bold uppercase tracking-wider text-slate-500">Expense Claims</h3></div>
        {(req.expenses || []).length === 0 ? <div className="p-4 text-xs text-slate-400">No expense claims submitted.</div> : (
          <div className="divide-y divide-slate-100">
            {req.expenses.map((ex) => (
              <ExpenseBlock key={ex.id} ex={ex} canApprove={req.can_approve} onChange={load} />
            ))}
          </div>
        )}
      </div>

      {showExpense && <ExpenseForm rid={rid} onClose={() => setShowExpense(false)} onSaved={() => { setShowExpense(false); load(); }} />}
      <style>{`.inp{width:100%;border:1px solid #e2e8f0;border-radius:2px;padding:8px 10px;font-size:13px;outline:none}.inp:focus{border-color:#3b4872}.lbl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:#64748b;display:block;margin-bottom:4px}`}</style>
    </div>
  );
}

function ExpenseBlock({ ex, canApprove, onChange }) {
  const act = async (verb, body, msg) => {
    try { await apiPost(`/hr/travel/expenses/${ex.id}/${verb}`, body || {}); toast.success(msg); onChange(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="p-4" data-testid={`expense-block-${ex.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs"><span className="font-display font-bold">Claim · {formatINR(ex.total_claimed)}</span> <span className="text-slate-400">(advance adj {formatINR(ex.advance_adjusted)})</span></div>
        <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm ${ex.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : ex.status === 'paid' ? 'bg-blue-100 text-blue-700' : ex.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>{ex.status}</span>
      </div>
      <div className="mt-2 space-y-1">
        {(ex.line_items || []).map((li, i) => (
          <div key={i} className="flex items-center justify-between text-[11px] text-slate-600" data-testid={`exp-line-${ex.id}-${i}`}>
            <span>{li.category} · {li.description || ''}</span>
            <span className="flex items-center gap-2">{formatINR(li.amount)}{li.receipt_url && <a href="#" onClick={(e) => { e.preventDefault(); window.open(`${process.env.REACT_APP_BACKEND_URL}/api/hr/travel/receipt?path=${encodeURIComponent(li.receipt_url)}`, '_blank'); }} className="text-[#3b4872]"><Paperclip size={11} /></a>}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 text-xs font-display font-bold text-[#3b4872]" data-testid={`exp-net-${ex.id}`}>Net payable: {formatINR(ex.net_payable)}</div>
      {canApprove && ex.status === 'submitted' && (
        <div className="flex gap-2 mt-2">
          <button onClick={() => act('approve', {}, 'Expense approved')} data-testid={`exp-approve-${ex.id}`} className="px-3 py-1.5 bg-emerald-600 text-white rounded-sm text-[10px] font-display font-bold uppercase tracking-wider">Approve</button>
          <button onClick={() => { const r = window.prompt('Reason:'); if (r) act('reject', { rejection_reason: r }, 'Rejected'); }} data-testid={`exp-reject-${ex.id}`} className="px-3 py-1.5 border border-red-300 text-red-600 rounded-sm text-[10px] font-display font-bold uppercase tracking-wider">Reject</button>
        </div>
      )}
      {canApprove && ex.status === 'approved' && (
        <button onClick={() => act('mark-paid', {}, 'Marked paid')} data-testid={`exp-paid-${ex.id}`} className="mt-2 px-3 py-1.5 bg-blue-600 text-white rounded-sm text-[10px] font-display font-bold uppercase tracking-wider">Mark Paid</button>
      )}
    </div>
  );
}

function ExpenseForm({ rid, onClose, onSaved }) {
  const [lines, setLines] = useState([{ category: 'hotel', description: '', amount: '', receipt_url: null, date: '' }]);
  const [advance, setAdvance] = useState('');
  const [busy, setBusy] = useState(false);

  const total = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const net = total - (Number(advance) || 0);

  const setLine = (i, patch) => setLines((p) => p.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const upload = async (i, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await apiUpload('/hr/travel/receipt', fd); setLine(i, { receipt_url: r.receipt_url }); toast.success('Receipt attached'); }
    catch { toast.error('Upload failed'); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await apiPost(`/hr/travel/requests/${rid}/expenses`, {
        line_items: lines.map((l) => ({ ...l, amount: Number(l.amount) || 0 })),
        advance_adjusted: Number(advance) || 0,
      });
      toast.success('Expense claim submitted'); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="expense-form-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-lg p-5 space-y-3 max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between"><h3 className="font-display font-bold text-sm">Submit Expense Claim</h3><button onClick={onClose}><X size={18} /></button></div>
        <div className="flex-1 overflow-y-auto space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`expform-line-${i}`}>
              <select className="inp col-span-3" value={l.category} onChange={(e) => setLine(i, { category: e.target.value })}>{EXP_CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
              <input className="inp col-span-4" placeholder="Description" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} />
              <input type="number" className="inp col-span-2" placeholder="Amt" value={l.amount} onChange={(e) => setLine(i, { amount: e.target.value })} data-testid={`expform-amt-${i}`} />
              <label className="col-span-2 text-[10px] text-slate-500 cursor-pointer flex items-center gap-1"><Paperclip size={12} />{l.receipt_url ? 'ok' : 'receipt'}<input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => upload(i, e.target.files[0])} /></label>
              <button onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="col-span-1 text-slate-400"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => setLines([...lines, { category: 'other', description: '', amount: '', receipt_url: null, date: '' }])} data-testid="add-line-btn" className="text-xs text-[#3b4872] font-bold flex items-center gap-1"><Plus size={13} /> Add line</button>
        </div>
        <div className="border-t pt-2 space-y-1 text-xs">
          <div className="flex justify-between"><span>Total claimed</span><span className="font-display font-bold">{formatINR(total)}</span></div>
          <label className="flex justify-between items-center"><span>Advance adjusted</span><input type="number" className="inp w-28 text-right" value={advance} onChange={(e) => setAdvance(e.target.value)} data-testid="advance-adjusted-input" /></label>
          <div className="flex justify-between text-sm font-display font-bold text-[#3b4872]" data-testid="net-payable-preview"><span>Net payable</span><span>{formatINR(net)}</span></div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="submit-expense-btn" className="w-full py-2.5 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50">{busy ? 'Submitting…' : 'Submit Claim'}</button>
        <style>{`.inp{border:1px solid #e2e8f0;border-radius:2px;padding:6px 8px;font-size:12px;outline:none}.inp:focus{border-color:#3b4872}`}</style>
      </div>
    </div>
  );
}

const Info = ({ label, v }) => v ? (
  <div className="bg-white border border-slate-200 rounded-sm px-3 py-2">
    <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="text-sm mt-0.5">{v}</div>
  </div>
) : null;
